import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { optionsService, type SearchOptionRow } from '../services/optionsService';
import type { FormSearchMeta } from '../types';

const DEBOUNCE_MS = 500;

interface UseSearchOptionsArgs {
  searchMeta?: FormSearchMeta;
  searchValue: string;
  /** When set, skip the typeahead query (used for initial label resolution). */
  enabled?: boolean;
}

/**
 * Debounced react-query for `/utilities/options/:options_id`. Returns rows
 * mapped via `optionsService.search` so the renderer never touches raw NocoDB
 * shapes.
 */
export const useSearchOptions = ({ searchMeta, searchValue, enabled = true }: UseSearchOptionsArgs) => {
  const [debounced, setDebounced] = useState(searchValue);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(searchValue), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchValue]);

  return useQuery({
    queryKey: [
      'utilities-options',
      searchMeta?.options_id,
      searchMeta?.specific_type ?? null,
      searchMeta?.search_space ?? null,
      searchMeta?.search_column ?? searchMeta?.primary_field ?? null,
      debounced,
    ],
    queryFn: async (): Promise<SearchOptionRow[]> => {
      if (!searchMeta) return [];
      return optionsService.search({ searchMeta, searchValue: debounced });
    },
    enabled: enabled && !!searchMeta,
    staleTime: 60 * 1000,
    placeholderData: (prev) => prev,
  });
};

/**
 * One-shot resolution of a stored value back into a display row (used when
 * the form opens with a pre-populated value).
 */
export const useResolvePrePopulatedOption = ({
  searchMeta,
  prePopulatedValue,
}: {
  searchMeta?: FormSearchMeta;
  prePopulatedValue: string | number | null | undefined;
}) => {
  return useQuery({
    queryKey: [
      'utilities-options-prepopulated',
      searchMeta?.options_id,
      searchMeta?.key,
      prePopulatedValue ?? null,
    ],
    queryFn: async (): Promise<SearchOptionRow | undefined> => {
      if (!searchMeta || prePopulatedValue === undefined || prePopulatedValue === null || prePopulatedValue === '') {
        return undefined;
      }
      const rows = await optionsService.search({ searchMeta, prePopulatedValue });
      return rows[0];
    },
    enabled:
      !!searchMeta &&
      prePopulatedValue !== undefined &&
      prePopulatedValue !== null &&
      prePopulatedValue !== '',
    staleTime: 5 * 60 * 1000,
  });
};
